import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Widget from './Widget';
import {loadFromLocalStorage, deepCopy, updateWidgetPreferences} from '../../imports';
const {isEmpty} = require('lodash');

const CLASS_NAMES = {
    WIDGET_BOX: 'widget-box',
    WIDGET_CONTAINER: 'widget-container',
    WIDGET_NEW: 'widget-new'
};

/**
 * Dashboard widget layout.
 *
 * Replaced `react-grid-layout` (which we only used as a layout calculator —
 * drag/resize were already disabled and reorder was driven by HTML5 DnD on
 * each `.widget-box`) with a 12-column CSS Grid. Each widget exposes its
 * column span via the `--rgw` (regular) and `--smw` (small breakpoint) CSS
 * variables and its row height via inline `height`. CSS Grid auto-flow
 * handles wrapping and vertical compaction natively, and the existing HTML5
 * DnD swap logic is preserved verbatim.
 */
class WidgetContainer extends Component {
    constructor(props) {
        super(props);
        const raw = props.widgetDefinitions;
        const widgetDefs = Array.isArray(raw) ? deepCopy(raw) : [];
        this.state = {widgetDefs};
    }

    onSwap = widgetDefinitions => this.props.updateWidgetPreferences(widgetDefinitions);

    allowDrop = (ev) => {
        ev.preventDefault();
    }

    // Use currentTarget (always the .widget-box the listener is bound to), not
    // target (the deepest element under the cursor). Without this, dragging
    // from inside the widget content -- e.g. a chart -- yields an empty id and
    // the swap silently fails. Same applies to data-static lookup.
    drag = (ev) => {
        const box = ev.currentTarget;
        if (String(box.getAttribute('data-static')) === 'true') {
            ev.preventDefault();
            ev.returnValue = false;
            return false;
        }
        ev.dataTransfer.setData('text', box.getAttribute('data-order-def') || '');
    }

    drop = (ev) => {
        ev.preventDefault();
        const sourceOrderDef = ev.dataTransfer.getData('text');
        const targetOrderDef = ev.currentTarget.getAttribute('data-order-def');
        if (!sourceOrderDef || !targetOrderDef || sourceOrderDef === targetOrderDef) {
            return;
        }
        if (String(ev.currentTarget.getAttribute('data-static')) === 'true') {
            return;
        }
        this.swap(sourceOrderDef, targetOrderDef);
    }

    swap(sourceOrderDef, targetOrderDef) {
        const {widgetDefs} = this.state;
        if (!Array.isArray(widgetDefs) || isEmpty(widgetDefs)) {
            return;
        }
        const sInd = widgetDefs.findIndex(el => el.i === sourceOrderDef);
        const tInd = widgetDefs.findIndex(el => el.i === targetOrderDef);
        if (sInd === -1 || tInd === -1) {
            return;
        }
        const next = [...widgetDefs];
        const tmp = next[sInd];
        next[sInd] = next[tInd];
        next[tInd] = tmp;

        this.onSwap(next);
        this.setState({widgetDefs: next});
        this.props.rerenderWidgets(false);
    }

    render() {
        const {publisherId, period} = this.props;
        const {widgetDefs} = this.state;
        if (!Array.isArray(widgetDefs) || isEmpty(widgetDefs)) {
            return null;
        }

        const lsWidgets = JSON.parse(loadFromLocalStorage('newlyAddedLibraryWidget')) || [];

        return (
            <div className={CLASS_NAMES.WIDGET_CONTAINER}>
                {widgetDefs.map((widgetDef, index) => {
                    const isNewlyAdded = lsWidgets.find(x => x == widgetDef.i);
                    const isNewClassName = isNewlyAdded ? CLASS_NAMES.WIDGET_NEW : '';
                    const widgetClassNames = classNames(CLASS_NAMES.WIDGET_BOX, widgetDef.kind);
                    const h = widgetDef.height ? widgetDef.height : widgetDef.h;
                    const isStatic = !!widgetDef.static;
                    const style = {
                        '--rgw': widgetDef.regularWidth,
                        '--smw': widgetDef.smallWidth,
                        height: h ? `${h}px` : undefined
                    };
                    return (
                        <div
                            className={widgetClassNames}
                            id={String(index)}
                            key={widgetDef.i}
                            draggable={!isStatic}
                            onDragStart={this.drag}
                            onDrop={this.drop}
                            onDragOver={this.allowDrop}
                            data-static={isStatic}
                            data-order-def={widgetDef.i}
                            style={style}
                        >
                            <Widget
                                type={widgetDef.type}
                                publisherId={publisherId}
                                initialPeriod={period}
                                isNewClassName={isNewClassName}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

WidgetContainer.propTypes = {
    widgetDefinitions: PropTypes.array,
    period: PropTypes.object,
    publisherId: PropTypes.number,
    updateWidgetPreferences: PropTypes.func,
    rerenderWidgets: PropTypes.func
};

const mapStateToProps = (state) => ({
    widgetPreferences: state.dashboard.widgetPreferences
});

const mapDispatchToProps = ({
    updateWidgetPreferences
});
export default (connect(mapStateToProps, mapDispatchToProps)(WidgetContainer));
